// Production (M5, WP5.2): the batches of one day at a branch — planned, started, completed,
// cancelled — with what came out against what was planned. Plan a simple batch here; the
// full planning board with feasibility checks is Planning's job.

import type { BatchSummary } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import { BATCH_STATUSES } from '@enp/domain/production';
import { addDays } from '@enp/domain/time';
import {
  Button,
  cn,
  createColumnHelper,
  DataTable,
  DateField,
  EmptyState,
  formatTime,
  IconButton,
  PageHeader,
  toast,
  type ColumnDef,
} from '@enp/ui';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChefHat, ChevronLeft, ChevronRight, Plus, SearchX, TriangleAlert, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import {
  BatchStatusBadge,
  NARROW_ONLY,
  ProductionViews,
  useCan,
  Variance,
  WIDE_COLUMN,
} from '../../../production/parts.js';
import { PlanBatchDialog } from '../../../production/plan-dialog.js';
import {
  batchesQuery,
  batchesSearchSchema,
  type BatchesSearch,
} from '../../../production/queries.js';
import { amountText, BATCH_STATUS, flagShortText } from '../../../production/words.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { FilterSelect, LoadMoreFooter } from '../../../stock/components.js';
import { accountToday, plainDateLabel } from '../../../stock/labels.js';
import { clean, locationsQuery } from '../../../stock/queries.js';

export const Route = createFileRoute('/_app/production/')({
  validateSearch: batchesSearchSchema,
  component: ProductionPage,
});

const TABLE_LABEL = 'Batches';

/** Where a batch is made: "Main bakery · Production 1 · from a station". */
function whereText(b: BatchSummary): string {
  return [
    b.branch.name,
    b.station?.name ?? 'Any station',
    b.origin === 'station' && 'from a station',
  ]
    .filter(Boolean)
    .join(' · ');
}

// Priority columns below 1360 px (UI5A-103, parts.tsx): Where folds under the product, the
// output lot under what was made.
function columns(timeZone: string): ColumnDef<BatchSummary, unknown>[] {
  const col = createColumnHelper<BatchSummary>();
  return [
    col.display({
      id: 'start',
      header: 'Starts',
      cell: ({ row }) => (
        <span className="flex flex-col whitespace-nowrap leading-tight">
          <time dateTime={row.original.planned_start} className="tabular-nums text-fg">
            {formatTime(row.original.planned_start, { timeZone })}
          </time>
          <span className="font-mono text-xs text-muted">{row.original.number}</span>
        </span>
      ),
    }),
    col.display({
      id: 'product',
      header: 'Product',
      meta: { width: 'min-w-48' },
      cell: ({ row }) => (
        <span className="flex flex-col leading-tight">
          <Link
            to="/production/$batchId"
            params={{ batchId: row.original.id }}
            className="font-medium text-fg hover:underline"
          >
            {row.original.product.name}
          </Link>
          <span className="text-xs text-muted">
            Recipe version {row.original.recipe_version.version_no}
          </span>
          <span className={cn('text-xs text-muted', NARROW_ONLY)} data-testid="where-folded">
            {whereText(row.original)}
          </span>
        </span>
      ),
    }),
    col.display({
      id: 'planned',
      header: 'Planned',
      meta: { align: 'right' },
      // With its unit (UI5A-16): "20 each", "2 kg".
      cell: ({ row }) => (
        <span className="whitespace-nowrap tabular-nums">
          {amountText(row.original.planned_qty, row.original.product.base_uom)}
        </span>
      ),
    }),
    col.display({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <BatchStatusBadge status={row.original.status} />,
    }),
    col.display({
      id: 'made',
      header: 'Made',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const b = row.original;
        if (b.actual_yield_qty === null) return <span className="text-muted">—</span>;
        return (
          <span className="flex flex-col items-end leading-tight">
            {b.output_lot && (
              <span className={cn('order-last font-mono text-xs text-muted', NARROW_ONLY)}>
                {b.output_lot.lot_code}
              </span>
            )}
            <span className="whitespace-nowrap font-medium tabular-nums">
              {amountText(b.actual_yield_qty, b.product.base_uom)}
            </span>
            {b.yield_variance && (
              <Variance
                value={b.yield_variance.vs_planned}
                unit={b.product.base_uom}
                className="text-xs"
              />
            )}
          </span>
        );
      },
    }),
    col.display({
      id: 'lot',
      header: 'Output lot',
      meta: { className: WIDE_COLUMN },
      cell: ({ row }) =>
        row.original.output_lot ? (
          <span className="whitespace-nowrap font-mono text-sm text-fg">
            {row.original.output_lot.lot_code}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    }),
    col.display({
      id: 'flags',
      header: 'Flags',
      // What kind of flag, not only how many (UI5A-9).
      cell: ({ row }) =>
        row.original.flag_count > 0 ? (
          <span className="inline-flex items-start gap-1 text-fg">
            <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-state-warning" />
            <span className="flex flex-col leading-tight">
              {row.original.flag_codes.slice(0, 2).map((code) => (
                <span key={code} className="whitespace-nowrap">
                  {flagShortText(code)}
                </span>
              ))}
              {row.original.flag_codes.length > 2 && (
                <span className="text-xs text-muted">
                  and {row.original.flag_codes.length - 2} more
                </span>
              )}
            </span>
          </span>
        ) : (
          <span className="text-muted">None</span>
        ),
    }),
    col.display({
      id: 'where',
      header: 'Where',
      // Folded under the product below 1360 px, never dropped (UI5A-20, UI5A-103).
      meta: { className: WIDE_COLUMN },
      cell: ({ row }) => (
        <span className="flex flex-col leading-tight">
          <span className="text-fg">{row.original.branch.name}</span>
          <span className="text-xs text-muted">
            {row.original.station?.name ?? 'Any station'}
            {row.original.origin === 'station' && ' · from a station'}
          </span>
        </span>
      ),
    }),
  ];
}

function ProductionPage() {
  useDocumentTitle('Production');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const timeZone = me.account.timezone;
  const today = accountToday(me.account);
  const day = search.day ?? today;
  const canPlan = useCan('production.plan');
  const [planning, setPlanning] = useState(false);
  const setSearch = (patch: Partial<BatchesSearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });
  const setDay = (d: string | undefined) => setSearch({ day: d && d !== today ? d : undefined });

  const locations = useQuery(locationsQuery());
  const branches = useMemo(() => {
    const seen = new Map<string, string>();
    for (const l of locations.data?.items ?? []) seen.set(l.branch.id, l.branch.name);
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [locations.data]);

  const query = { day, branch: search.branch, status: search.status, product: search.product };
  const list = useInfiniteQuery({ ...batchesQuery(query), placeholderData: keepPreviousData });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const filtered = Boolean(search.status || search.branch || search.product);
  const dayText = plainDateLabel(day, today);
  // Nothing on the day: the empty state's "Plan a batch" is the one action (UI8C-6).
  const emptyDay =
    !filtered && list.data !== undefined && !list.isPlaceholderData && items.length === 0;

  const counts = useMemo(() => {
    const c = { planned: 0, started: 0, completed: 0, cancelled: 0 };
    for (const b of items) c[b.status] += 1;
    return c;
  }, [items]);

  const empty = filtered ? (
    <EmptyState
      compact
      icon={SearchX}
      title="No batches match"
      description={`No batch on ${dayText} fits these filters.`}
      action={
        <Button
          variant="secondary"
          icon={<X aria-hidden />}
          onClick={() => setSearch({ status: undefined, branch: undefined, product: undefined })}
        >
          Clear filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      compact
      icon={ChefHat}
      title={`Nothing planned for ${day === today ? 'today' : dayText}`}
      description="Plan a batch here, or the kitchen starts one at a production station. Only products with a signed-off recipe can be planned."
      action={
        canPlan ? (
          <Button icon={<Plus aria-hidden />} onClick={() => setPlanning(true)}>
            Plan a batch
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <>
      <PageHeader
        title="Production"
        description="What the kitchen plans and makes each day, and what came out."
        actions={
          canPlan &&
          !emptyDay && (
            <Button icon={<Plus aria-hidden />} onClick={() => setPlanning(true)}>
              Plan a batch
            </Button>
          )
        }
      />
      <ProductionViews current="batches" />

      <div
        className="mb-3 flex flex-wrap items-center gap-2"
        role="search"
        aria-label="Batch filters"
      >
        <div className="flex items-center gap-1">
          <IconButton
            label="Previous day"
            variant="secondary"
            icon={<ChevronLeft aria-hidden />}
            onClick={() => setDay(addDays(day, -1))}
          />
          <label htmlFor="batch-day" className="sr-only">
            Day
          </label>
          <DateField
            id="batch-day"
            value={day}
            today={today}
            onChange={(d) => d && setDay(d)}
            wrapperClassName="w-40"
          />
          <IconButton
            label="Next day"
            variant="secondary"
            icon={<ChevronRight aria-hidden />}
            onClick={() => setDay(addDays(day, 1))}
          />
        </div>
        {day !== today && (
          <Button variant="ghost" onClick={() => setDay(undefined)}>
            Today
          </Button>
        )}
        {branches.length > 1 && (
          <FilterSelect
            label="Branch"
            value={search.branch}
            allLabel="Every branch"
            options={branches}
            onChange={(v) => setSearch({ branch: v })}
          />
        )}
        <FilterSelect
          label="Status"
          value={search.status}
          allLabel="Every status"
          options={BATCH_STATUSES.map((s) => ({ value: s, label: BATCH_STATUS[s].label }))}
          onChange={(v) => setSearch({ status: v as BatchesSearch['status'] })}
        />
        {filtered && (
          <Button
            variant="ghost"
            icon={<X aria-hidden />}
            onClick={() => setSearch({ status: undefined, branch: undefined, product: undefined })}
          >
            Clear
          </Button>
        )}
      </div>

      {!search.status && items.length > 0 && (
        <p className="mb-3 text-sm text-muted" aria-live="polite">
          {day === today ? 'Today' : dayText}:{' '}
          {BATCH_STATUSES.filter((s) => counts[s] > 0)
            .map((s) => `${formatCount(counts[s])} ${BATCH_STATUS[s].label.toLowerCase()}`)
            .join(' · ')}
          {list.hasNextPage && ' (more below)'}
        </p>
      )}

      {list.isError && !list.data ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The batches"
          onRetry={() => void list.refetch()}
        />
      ) : (
        <DataTable
          label={TABLE_LABEL}
          columns={columns(timeZone)}
          data={items}
          loading={list.isPending}
          empty={empty}
          filterable={false}
          getRowId={(b) => b.id}
          onRowActivate={(b) =>
            void navigate({ to: '/production/$batchId', params: { batchId: b.id } })
          }
          footer={
            !list.isPending && items.length > 0 ? (
              <LoadMoreFooter
                loaded={items.length}
                total={list.data?.pages[0]?.total}
                noun={['batch', 'batches']}
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                updating={list.isFetching && !list.isFetchingNextPage}
                failed={list.isError && Boolean(list.data)}
                onMore={() => void list.fetchNextPage()}
              />
            ) : undefined
          }
        />
      )}

      {planning && (
        <PlanBatchDialog
          day={day}
          today={today}
          timeZone={timeZone}
          branchId={search.branch}
          onClose={() => setPlanning(false)}
          onPlanned={(b) => {
            setPlanning(false);
            toast.success(`Batch ${b.number} planned`, {
              description: `${b.product.name}, ${plainDateLabel(b.planned_day, today)} at ${formatTime(b.planned_start, { timeZone })}.`,
              action: {
                label: 'Open',
                onClick: () =>
                  void navigate({ to: '/production/$batchId', params: { batchId: b.id } }),
              },
            });
            if (b.planned_day !== day) setDay(b.planned_day);
          }}
        />
      )}
    </>
  );
}
